import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Plus, ChevronDown, ChevronUp, Edit2, Loader2, Sparkles,
  Users, Package, Percent
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SAAS_CATEGORIES = ['Microsoft 365', 'Google Workspace', 'Email Hosting'];

const CATEGORY_META = {
  'Microsoft 365': { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: '🪟' },
  'Google Workspace': { color: 'bg-red-100 text-red-700 border-red-200', icon: '🔤' },
  'Email Hosting': { color: 'bg-amber-100 text-amber-700 border-amber-200', icon: '📧' },
};

const BILLING_CYCLES = [
  { key: 'monthly',     label: 'Monthly' },
  { key: 'quarterly',   label: 'Quarterly' },
  { key: 'half_yearly', label: 'Half Yearly' },
  { key: 'yearly',      label: 'Yearly' },
];

const PRICING_UNITS = [
  { value: 'per_user', label: 'Per User' },
];

const emptyForm = {
  name: '',
  description: '',
  category: 'Microsoft 365',
  product_type: 'saas',
  pricing_unit: 'per_user',
  base_price: '',
  billing_prices: { monthly: '', quarterly: '', half_yearly: '', yearly: '' },
  enabled_billing_cycles: ['monthly', 'quarterly', 'half_yearly', 'yearly'],
  features: '',
  default_markup_percents: { monthly: '', quarterly: '', half_yearly: '', yearly: '' },
  max_quantity: '',
};

export default function CatalogProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [catFilter, setCatFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchProducts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/products`);
      const saas = res.data.filter(p => SAAS_CATEGORIES.includes(p.category));
      setProducts(saas);
    } catch {
      toast.error('Failed to load catalog products');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const res = await axios.post(`${API}/admin/seed-catalog-products`);
      const { inserted, total_inserted } = res.data;
      if (total_inserted === 0) {
        toast.info('All catalog products already exist');
      } else {
        toast.success(`Added ${total_inserted} product${total_inserted > 1 ? 's' : ''}: ${inserted.join(', ')}`);
      }
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Seeding failed');
    } finally {
      setSeeding(false);
    }
  };

  const openAdd = () => {
    setEditProduct(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditProduct(p);
    setForm({
      name: p.name,
      description: p.description || '',
      category: p.category,
      product_type: p.product_type || 'saas',
      pricing_unit: p.pricing_unit || 'per_user',
      base_price: p.base_price?.toString() || '',
      billing_prices: {
        monthly: p.billing_prices?.monthly ?? '',
        quarterly: p.billing_prices?.quarterly ?? '',
        half_yearly: p.billing_prices?.half_yearly ?? '',
        yearly: p.billing_prices?.yearly ?? '',
      },
      enabled_billing_cycles: p.enabled_billing_cycles || ['monthly'],
      features: (p.features || []).join(', '),
      default_markup_percents: {
        monthly: p.default_markup_percents?.monthly ?? '',
        quarterly: p.default_markup_percents?.quarterly ?? '',
        half_yearly: p.default_markup_percents?.half_yearly ?? '',
        yearly: p.default_markup_percents?.yearly ?? '',
      },
      max_quantity: p.max_quantity?.toString() || '',
    });
    setDialogOpen(true);
  };

  const handleToggleCycle = (key) => {
    setForm(f => ({
      ...f,
      enabled_billing_cycles: f.enabled_billing_cycles.includes(key)
        ? f.enabled_billing_cycles.filter(c => c !== key)
        : [...f.enabled_billing_cycles, key],
    }));
  };

  const handleToggleActive = async (product) => {
    try {
      await axios.put(`${API}/admin/products/${product.id}`, {
        ...product,
        is_active: !product.is_active,
        features: product.features || [],
        enabled_billing_cycles: product.enabled_billing_cycles || ['monthly'],
      });
      toast.success(`Product ${product.is_active ? 'deactivated' : 'activated'}`);
      fetchProducts();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Product name is required'); return; }
    if (form.enabled_billing_cycles.length === 0) { toast.error('Enable at least one billing cycle'); return; }

    const markup = {};
    Object.entries(form.default_markup_percents).forEach(([k, v]) => {
      if (v !== '' && !isNaN(Number(v))) markup[k] = Number(v);
    });

    const bp = {};
    form.enabled_billing_cycles.forEach(k => {
      if (form.billing_prices[k] !== '' && !isNaN(Number(form.billing_prices[k]))) {
        bp[k] = Number(form.billing_prices[k]);
      }
    });

    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      category: form.category,
      product_type: 'saas',
      pricing_unit: 'per_user',
      base_price: Number(form.base_price) || (bp.monthly || 0),
      billing_prices: bp,
      enabled_billing_cycles: form.enabled_billing_cycles,
      features: form.features.split(',').map(f => f.trim()).filter(Boolean),
      default_markup_percents: markup,
    };

    setSaving(true);
    try {
      if (editProduct) {
        const res = await axios.put(`${API}/admin/products/${editProduct.id}`, payload);
        const warnings = res.data?.below_cost_warnings || [];
        if (warnings.length > 0) {
          toast.warning(
            `Price updated — ${warnings.length} partner${warnings.length > 1 ? 's have' : ' has'} selling price below new wholesale: ` +
            warnings.map(w => w.partner_name).join(', '),
            { duration: 8000 }
          );
        } else {
          toast.success('Product updated');
        }
      } else {
        await axios.post(`${API}/admin/products`, payload);
        toast.success('Product created');
      }
      setDialogOpen(false);
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const filtered = catFilter === 'all'
    ? products
    : products.filter(p => p.category === catFilter);

  const pricingUnitLabel = (unit) =>
    unit === 'per_user' ? '/user' : unit === 'per_account' ? '/acc' : '';

  const lowestPrice = (p) => {
    const prices = Object.values(p.billing_prices || {}).filter(v => v > 0);
    return prices.length > 0 ? Math.min(...prices) : p.base_price;
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-2 flex-wrap">
          {['all', ...SAAS_CATEGORIES].map(cat => (
            <button
              key={cat}
              onClick={() => setCatFilter(cat)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors border ${
                catFilter === cat
                  ? 'bg-slate-800 text-white border-slate-800'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
              }`}
            >
              {cat === 'all' ? 'All Products' : cat}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleSeed}
            disabled={seeding}
            data-testid="seed-catalog-btn"
          >
            {seeding ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
            Load Default Catalog
          </Button>
          <Button size="sm" onClick={openAdd} data-testid="add-catalog-product-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Add Product
          </Button>
        </div>
      </div>

      {/* Products grid */}
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      ) : filtered.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-16 flex flex-col items-center gap-4 text-center">
            <Package className="h-10 w-10 text-slate-300" />
            <div>
              <p className="font-medium text-slate-700">No catalog products yet</p>
              <p className="text-sm text-slate-400 mt-1">Click "Load Default Catalog" to add all 9 products instantly</p>
            </div>
            <Button onClick={handleSeed} disabled={seeding} className="mt-2">
              {seeding ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
              Load Default Catalog
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(p => {
            const meta = CATEGORY_META[p.category] || { color: 'bg-slate-100 text-slate-700', icon: '📦' };
            const isExp = expanded === p.id;
            const unit = pricingUnitLabel(p.pricing_unit);

            return (
              <Card key={p.id} className="bg-white border border-slate-200 shadow-sm overflow-hidden" data-testid={`catalog-product-${p.id}`}>
                {/* Main row */}
                <div className="flex items-center px-4 py-3 gap-3">
                  {/* Expand toggle */}
                  <button
                    onClick={() => setExpanded(isExp ? null : p.id)}
                    className="p-1 rounded bg-blue-600 hover:bg-blue-700 shrink-0"
                    data-testid={`expand-catalog-${p.id}`}
                  >
                    {isExp ? <ChevronUp className="h-4 w-4 text-white" /> : <ChevronDown className="h-4 w-4 text-white" />}
                  </button>

                  {/* Product name + badge */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slate-800">{p.name}</span>
                      <Badge variant="outline" className={`text-xs shrink-0 ${meta.color}`}>
                        {meta.icon} {p.category}
                      </Badge>
                      <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200 shrink-0">
                        <Users className="h-3 w-3 mr-1" />
                        Per User
                      </Badge>
                    </div>
                  </div>

                  {/* Starting price */}
                  <div className="text-right shrink-0 hidden sm:block">
                    <div className="text-xs text-slate-400">Starting from</div>
                    <div className="font-semibold text-slate-800">
                      ₹{lowestPrice(p).toLocaleString()}{unit}/mo
                    </div>
                  </div>

                  {/* Status toggle */}
                  <Switch
                    checked={p.is_active !== false}
                    onCheckedChange={() => handleToggleActive(p)}
                    className="shrink-0"
                    data-testid={`catalog-toggle-${p.id}`}
                  />

                  {/* Edit */}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openEdit(p)}
                    className="shrink-0"
                    data-testid={`edit-catalog-${p.id}`}
                  >
                    <Edit2 className="h-3.5 w-3.5 mr-1" /> Edit
                  </Button>
                </div>

                {/* Expandable detail */}
                {isExp && (
                  <div className="border-t border-slate-100 px-4 pb-4 pt-3 space-y-4 bg-slate-50">
                    <p className="text-sm text-slate-600">{p.description}</p>

                    {/* Features */}
                    {p.features?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {p.features.map((f, i) => (
                          <span key={i} className="text-xs bg-white border border-slate-200 rounded-full px-2.5 py-1 text-slate-600">
                            {f}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Billing cycles table */}
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Pricing & Default Markup</p>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {(p.enabled_billing_cycles || ['monthly']).map(cycle => {
                          const cycleLabel = BILLING_CYCLES.find(c => c.key === cycle)?.label || cycle;
                          const price = p.billing_prices?.[cycle] || 0;
                          const markup = p.default_markup_percents?.[cycle] || 0;
                          const retail = price * (1 + markup / 100);
                          return (
                            <div key={cycle} className="bg-white border border-slate-200 rounded-lg p-2.5 text-sm">
                              <div className="text-xs text-slate-400 mb-1">{cycleLabel}</div>
                              <div className="font-semibold text-slate-800">₹{price.toLocaleString()}<span className="text-xs font-normal text-slate-400">{unit}</span></div>
                              {markup > 0 && (
                                <>
                                  <div className="flex items-center gap-1 mt-1">
                                    <Percent className="h-3 w-3 text-green-500" />
                                    <span className="text-xs text-green-600 font-medium">{markup}% default markup</span>
                                  </div>
                                  <div className="text-xs text-slate-500 mt-0.5">Retail ≈ ₹{Math.round(retail).toLocaleString()}{unit}</div>
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!o) setDialogOpen(false); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="catalog-product-dialog">
          <DialogHeader>
            <DialogTitle>{editProduct ? 'Edit Catalog Product' : 'Add Catalog Product'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5 pt-1">
            {/* Name + Category */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Product Name *</Label>
                <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required data-testid="catalog-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Category *</Label>
                <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                  <SelectTrigger data-testid="catalog-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SAAS_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Description */}
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} data-testid="catalog-desc-input" />
            </div>

            {/* Billing Cycles with Wholesale + Markup */}
            <div className="space-y-2">
              <Label>Billing Cycles — Wholesale Price & Default Markup</Label>
              <div className="grid grid-cols-2 gap-3">
                {BILLING_CYCLES.map(({ key, label }) => {
                  const enabled = form.enabled_billing_cycles.includes(key);
                  return (
                    <div key={key} className={`p-3 rounded-lg border transition-colors ${enabled ? 'border-slate-200 bg-white' : 'border-dashed border-slate-200 bg-slate-50 opacity-60'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <input type="checkbox" checked={enabled} onChange={() => handleToggleCycle(key)} className="w-3.5 h-3.5 accent-blue-600 cursor-pointer" />
                        <span className={`text-sm font-medium ${enabled ? 'text-slate-700' : 'text-slate-400'}`}>{label}</span>
                      </div>
                      {enabled && (
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs text-slate-500">Wholesale ₹</Label>
                            <Input
                              type="number" step="0.01" min="0"
                              value={form.billing_prices[key]}
                              onChange={e => setForm(f => ({ ...f, billing_prices: { ...f.billing_prices, [key]: e.target.value } }))}
                              placeholder="Price"
                              className="h-8 text-sm mt-0.5"
                              data-testid={`catalog-price-${key}`}
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-slate-500">Default Markup %</Label>
                            <div className="relative mt-0.5">
                              <Input
                                type="number" step="0.1" min="0" max="1000"
                                value={form.default_markup_percents[key]}
                                onChange={e => setForm(f => ({ ...f, default_markup_percents: { ...f.default_markup_percents, [key]: e.target.value } }))}
                                placeholder="e.g. 18"
                                className="h-8 text-sm pr-6"
                                data-testid={`catalog-markup-${key}`}
                              />
                              <Percent className="absolute right-2 top-2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Features */}
            <div className="space-y-1.5">
              <Label>Features <span className="text-slate-400 text-xs">(comma-separated)</span></Label>
              <Input value={form.features} onChange={e => setForm(f => ({ ...f, features: e.target.value }))} placeholder="Feature 1, Feature 2, Feature 3" data-testid="catalog-features-input" />
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="save-catalog-product-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
                {editProduct ? 'Update Product' : 'Create Product'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
